import React, { useEffect, useState, useMemo } from "react";

import { useExecutionEngagementDetails } from "@/contexts/ExecutionEngagementDetailsContext";
import { applyLimitedReviewExecutionCardFilter } from "@/utils/execution-limited-review-ui";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { ShieldCheck, Layers, Lock, FileCheck2, Plus, XCircle, AlertCircle } from "lucide-react";
import DocumentListWithActions from "../../documents/DocumentListWithActions";
import { useWorkspaceDeficiencies } from "../../../hooks/useWorkspaceDeficiencies";
import { useWorkspaceMisstatements } from "../../../hooks/useWorkspaceMisstatements";
import { getFileSectionList } from "../../../utils/workspace-section-mapping";

type BorrowingsIndASLandingProps = {
  onSelect: (section: string) => void;
};

const baseCategories: Array<{
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
}> = [
  {
    id: "borindas-tailoring",
    title: "Tailoring Questions",
    description: "Customize the audit program for Borrowings - Ind AS.",
    icon: Layers,
    accent: "from-blue-500/20 to-indigo-500/20",
  },
  {
    id: "borindas-romms",
    title: "RoMMs",
    description: "Identify and assess risks for Borrowings - Ind AS.",
    icon: ShieldCheck,
    accent: "from-amber-500/20 to-orange-500/20",
  },
  {
    id: "borindas-controls",
    title: "Internal Controls",
    description: "Evaluate design and implementation of controls.",
    icon: Lock,
    accent: "from-emerald-500/20 to-teal-500/20",
  },
  {
    id: "borindas-substantive",
    title: "Substantive Procedures",
    description: "Perform detailed procedures over Borrowings - Ind AS.",
    icon: FileCheck2,
    accent: "from-fuchsia-500/20 to-purple-500/20",
  },
];

const getCategories = (hasDeficiencies: boolean, hasMisstatements: boolean) => {
  const categories = [...baseCategories];
  if (hasDeficiencies) {
    categories.push({
      id: "borindas-deficiencies",
      title: "Deficiencies",
      description:
        "View deficiencies in internal controls linked to this workspace.",
      icon: XCircle,
      accent: "from-red-500/20 to-rose-500/20",
    });
  }
  if (hasMisstatements) {
    categories.push({
      id: "borindas-misstatements",
      title: "Misstatements",
      description: "View misstatements linked to this workspace.",
      icon: AlertCircle,
      accent: "from-orange-500/20 to-amber-500/20",
    });
  }
  return categories;
};

export default function BorrowingsIndASLanding({ onSelect }: BorrowingsIndASLandingProps) {
  const workspaceName = "Borrowings - Ind AS";
  const {
    deficiencies: workspaceDeficiencies,
    loading: defLoading,
    hasDeficiencies,
  } = useWorkspaceDeficiencies(workspaceName);
  const {
    misstatements: workspaceMisstatements,
    loading: misLoading,
    hasMisstatements,
  } = useWorkspaceMisstatements(workspaceName);

  const [docs, setDocs] = useState<
    Array<{ name: string; url: string; prep_so: any; rev_so: any; conc_so: any; eqr_so: any; partner_so: any }>
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const loadDocs = async () => {
    try {
      setLoading(true);
      setError("");
      const sectionKey = "Execution_DEBT";
      const res = await (window as any)?.sharePointAPI?.loadDocumentListBySection?.(sectionKey);
      if (res?.success) {
        setDocs(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res?.error || "Failed to load document list");
      }
    } catch (e: any) {
      setError(e?.message || "Unexpected error while loading document list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const run = async () => { await loadDocs(); };
    run();
  }, []);

  const { isLimitedReviewExecution } = useExecutionEngagementDetails();
  const displayCategories = useMemo(
    () =>
      applyLimitedReviewExecutionCardFilter(
        getCategories(hasDeficiencies, hasMisstatements),
        isLimitedReviewExecution,
      ),
    [hasDeficiencies, hasMisstatements, isLimitedReviewExecution],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Borrowings - Ind AS</h2>
          <p className="text-sm text-white/60">
            Choose a workflow to proceed. You can return here anytime.
          </p>
        </div>
        <Badge className="bg-white/10 text-white/80">Execution</Badge>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {displayCategories.map((cat) => {
          const Icon = cat.icon;
          const isDeficiencyCard = cat.id === "borindas-deficiencies";
          const isMisstatementCard = cat.id === "borindas-misstatements";
          return (
            <Card
              key={cat.id}
              className="group cursor-pointer border-white/10 bg-white/5 text-white backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10"
              onClick={() => onSelect(cat.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div
                      className={`mb-3 inline-flex rounded-lg bg-gradient-to-br ${cat.accent} p-2`}
                    >
                      <Icon className="h-5 w-5 text-white/80" />
                    </div>
                    <div className="mb-1 text-sm font-semibold">{cat.title}</div>
                    <div className="mb-3 text-xs text-white/60">
                      {cat.description}
                    </div>
                  </div>
                  {(isDeficiencyCard || isMisstatementCard) && (
                    <Badge
                      variant="outline"
                      className="ml-auto text-[10px] uppercase tracking-wide"
                    >
                      {isDeficiencyCard
                        ? defLoading
                          ? "Loading"
                          : `${workspaceDeficiencies.length} item(s)`
                        : misLoading
                          ? "Loading"
                          : `${workspaceMisstatements.length} item(s)`}
                    </Badge>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/10 text-xs text-white/80 group-hover:bg-white/10 group-hover:text-white"
                >
                  Continue
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <DocumentListWithActions
        documents={docs}
        loading={loading}
        error={error}
        onRefresh={loadDocs}
        title="Document List"
        showUploadButton={true}
        uploadSectionList={getFileSectionList(workspaceName)}
        uploadFolderName="client"
        uploadMultiple={true}
        workspaceName={workspaceName}
        onNavigateToConfirmationLibrary={(w) => onSelect?.("confirmation-library:" + encodeURIComponent(w))}
        onNavigateToSamplingLibrary={(w) => onSelect?.("sampling-library:" + encodeURIComponent(w))}
      />
    </div>
  );
}

